import React, {useCallback, useEffect, useMemo, useState, useContext, useRef} from 'react';
import PropTypes from 'prop-types';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination , TableRow, TableSortLabel, styled, tableCellClasses, Paper, Backdrop, Typography, IconButton,
    CircularProgress, TextField, InputAdornment, Button, Stack, LinearProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip,
    MenuItem} from '@mui/material';
import { Edit, DeleteForever, Search, Add, ListAlt, Upload, Download } from '@mui/icons-material';
import Header from '../Components/layout/Header';
import DonateurForm from '../Components/forms/donateur-form';
import { API } from "../conf";
import { Context } from "../App";
import EditDonateurForm from '../Components/forms/edit-donator';
import RecapDonateur from '../Components/donateurs/recap-donateur';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
}));

function descendingComparator(a, b) {
  if (b < a) {
    return -1;
  }
  if (b > a) {
    return 1;
  }
  return 0;
}

function getComparator(order) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
}

function stableSort(array, table, orderBy, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = orderBy >-1 ? comparator(table[orderBy]?.sortBy(a[0]), table[orderBy]?.sortBy(b[0])) : 0;
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, table } =    props;
    
    const createSortHandler = (property) => (event) => onRequestSort(event, property)

    return (
        <TableHead>
        <StyledTableRow>
            {table.map((col, index) => (
            <StyledTableCell
                key={index}
                align='center'
                sx={{fontWeight:'bold'}}
                sortDirection={orderBy === index ? order : false}
            >
                    {col.sortable? <TableSortLabel
                    active={orderBy === index}
                    direction={orderBy === index ? order : 'asc'}
                    onClick={createSortHandler(index)}
                    >
                        {col.header}
                    </TableSortLabel> : col.header}
            </StyledTableCell>
            ))}
        </StyledTableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.number.isRequired,
};

export default function DonatorManager() {
    const { updateConnect } = useContext(Context)
    const [organisation, setOrganisation] = useState(JSON.parse(sessionStorage.getItem('eglise')))
    const [agent, setAgent] = useState(JSON.parse(sessionStorage.getItem('user')))
    const [eglises, setEglises] = useState([])
    const [egliseId, setEgliseId] = useState('')
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(1);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const [loading, setLoad] = useState(true)
    const [importing, setImport] = useState(false)
    const [importProgress, setImportProgress] = useState(null)
    const [importResult, setImportResult] = useState(null)
    const fileInputRef = useRef(null)
    const justImportedRef = useRef(false)
    const [add, setAdd] = useState(false)
    const [del, setDel] = useState(false)
    const [edit, setEdit] = useState(false)
    const [recap, setRecap] = useState(false)
    const [search_input, setSearch] = useState('')
    const [donators, setDonators] = useState([])
    const [donator, setDonator] = useState()
    const table = useCallback(()=>[
        {header: "Civilité", sortable: true, sortBy:(el)=>el.genre.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{width:'60px', textTransform:'capitalize'}}>{row.genre}</StyledTableCell>},
        {header: "Nom", sortable: true, sortBy:(el)=>el.nom.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'100px', textTransform:'capitalize'}}>{row.nom}</StyledTableCell>},
        {header: "Prénoms", sortable: true, sortBy:(el)=>(el.prenom1+(Boolean(el.prenom2)?' '+el.prenom2:'')).toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'100px', textTransform:'capitalize'}}>{row.prenom1+(Boolean(row.prenom2)?' '+row.prenom2:'')}</StyledTableCell>},
        {header: "Adresse e-mail", sortable: true, sortBy:(el)=>el.mail?.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'120px'}}>{row.mail?.toLowerCase()}</StyledTableCell>},
        {header: "Téléphone", sortable: true, sortBy:(el)=>el.tel, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'90px'}}>{row.tel}</StyledTableCell>},
        {header: "Adresse postale", sortable: true, sortBy:(el)=>el.ville?.toLowerCase(), row: (row, index)=> <StyledTableCell key={index} align="center" sx={{minWidth:'190px'}}>
            {row.voie&&<Typography variant='body1'>{row.voie}</Typography>}
            {row.adresse_complement&&<Typography variant='body1'>{row.adresse_complement}</Typography>}
            {(row.zip_code||row.ville)&&<Typography variant='body1'>{row.zip_code+' '+row.ville?.toUpperCase()}</Typography>}
        </StyledTableCell>},
        {header: "", sortable: false, row: (row, index)=><StyledTableCell key={index} align='left'>
            <IconButton aria-label="Récapitulatif" onClick={()=>{setDonator(row); setRecap(true)}}><ListAlt fontSize="small"/></IconButton>
            <IconButton aria-label="Edit" onClick={()=>{setDonator(row); setEdit(true)}} disabled={egliseId==='tous'}><Edit fontSize="small"/></IconButton>
            <IconButton aria-label="delete" onClick={()=>{setDonator(row); setDel(true)}} disabled={egliseId==='tous'&&agent.level<4}><DeleteForever color={'carmine'} fontSize="small"/></IconButton>
        </StyledTableCell>}
    ], [egliseId])

    const eglise = useMemo(()=>organisation&&organisation.type==='église' ? organisation : eglises.find(el=>el._id===egliseId), [egliseId, eglises, organisation])
    useEffect(()=>{if(organisation&&organisation.type==='église'&&eglises.some(el=>el._id===organisation._id)) sessionStorage.setItem('eglise', JSON.stringify(eglises.find(el=>el._id===organisation._id)))}, [eglises, organisation])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function normalize(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-.+@]/gi,'').toLowerCase()
    }

    function get_searchable(dnt) {
        return dnt.nom+' '+dnt.prenom1+(Boolean(dnt.prenom2)?' '+dnt.prenom2:'')+' '+dnt.mail+' '+dnt.tel+' '+dnt.voie+' '+dnt.adresse_complement+' '+dnt.zip_code+' '+dnt.ville
    }

    function handleDelete(){
        setDel(false); setLoad(true)
        fetch(API.concat(`/eglises/${eglise._id}/donateurs/delete/${donator.num}`), {
            method:'PATCH',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                updateConnect(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const json = await response.json()
                    setEglises(old=>[...old.filter(el=>el.number!==json.number), json])
                    setDonators(old=> old.filter(donat=> donat._id !== donator._id))
                    setDonator()
                }
                setLoad(false)
            }
        })
    }

    function updateDonateurs(newdonator){
        if(!donators.find(el => JSON.stringify(el)===JSON.stringify(newdonator))){
            setDonators(old => [...old, newdonator])
        }
    }

    function getAllDonators() {
        setLoad(true); setEgliseId('tous')
        fetch(API.concat('/donateurs'), {
            method:'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                updateConnect(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    const json = await response.json()
                    setDonators(json)
                }
                setLoad(false)
            }
        })
    }

    useEffect(()=>{
        if (justImportedRef.current) { justImportedRef.current = false; return }
        if(eglise?.donateurs) {
            fetch(API.concat(`/donateurs/by/${JSON.stringify({num:{$in: eglise.donateurs}, $and:[{nom: {$nin: ['anonyme', 'Anonyme', 'ANONYME']}}, {prenom1: {$nin: ['anonyme', 'Anonyme', 'ANONYME']}}]})}`), {
                method:'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async response=>{
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json()
                        setDonators(json)
                    }
                    setLoad(false)
                }
            })
        }
    }, [eglise])

    useEffect(()=>{
        if (organisation&&organisation.type==='fédération') {
            setLoad(true)
            fetch(API.concat(`/eglises/by/${JSON.stringify(agent.eglises?.length>0 ? {number:{$in: agent.eglises}}:{type: {$in: ['église', 'fédération']}})}`), {
                method:'GET',
                headers:{
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async(response) => {
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json();
                        setEglises(json)
                        setLoad(false)
                    }
                }
            })
        }
    }, [organisation, agent])

    useEffect(()=>{
        setOrganisation(JSON.parse(sessionStorage.getItem('eglise')))
        setAgent(JSON.parse(sessionStorage.getItem('user')))
    },[]) 

    const visibleRows = useMemo(() => stableSort(donators.filter(dnt=> normalize(get_searchable(dnt)).includes(normalize(search_input))), table(), orderBy, getComparator(order)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [donators, table, order, orderBy, page, rowsPerPage, search_input]);

    function handleExportCSV() {
        const headers = ['genre', 'Nom', 'Prénom', 'Téléphone', 'Email', "N° et libellé de voie", "Complément d'adresse", 'Code Postal', 'Ville']
        const rows = donators.map(d => [
            d.genre || '', d.nom || '', d.prenom1 || '', d.tel || '',
            d.mail || '', d.voie || '', d.adresse_complement || '',
            d.zip_code || '', d.ville || ''
        ])
        const csv = '﻿' + [headers, ...rows].map(r => r.join(';')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `donateurs_${eglise?.nom || 'export'}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    function handleImportCSV(e) {
        const file = e.target.files[0]
        if (!fileInputRef.current) return
        fileInputRef.current.value = ''
        if (!file) return
        const reader = new FileReader()
        reader.onload = async (evt) => {
            const text = evt.target.result.replace(/^﻿/, '')
            const lines = text.split('\n').map(l => l.trim().replace(/\r$/, '')).filter(l => l)
            if (lines.length < 2) return
            const headers = lines[0].split(';').map(h => h.trim())
            const headerMap = {
                'genre': 'genre', 'Nom': 'nom', 'Prénom': 'prenom1',
                'Téléphone': 'tel', 'Tel': 'tel', 'Email': 'mail',
                'N° et libellé de voie': 'voie',
                "Complément d'adresse": 'adresse_complement',
                'Code Postal': 'zip_code', 'Ville': 'ville'
            }
            const rows = lines.slice(1).map(line => {
                const values = line.split(';')
                return Object.fromEntries(headers.map((h, i) => [headerMap[h] || h, values[i]?.trim() || '']))
            }).filter(r => r.nom || r.prenom1)

            setImport(true)
            setImportProgress({ current: 0, total: rows.length })
            try {
                const response = await fetch(API.concat(`/donateurs/import/${eglise._id}`), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
                    body: JSON.stringify(rows)
                })
                const token = response.headers.get('x-auth-token')
                if (response.status === 401) { sessionStorage.clear(); updateConnect(false); return }
                if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)

                const streamReader = response.body.getReader()
                const decoder = new TextDecoder()
                let buffer = ''
                while (true) {
                    const { done, value } = await streamReader.read()
                    if (done) break
                    buffer += decoder.decode(value, { stream: true })
                    const chunks = buffer.split('\n')
                    buffer = chunks.pop()
                    for (const chunk of chunks) {
                        if (!chunk.trim()) continue
                        const event = JSON.parse(chunk)
                        if (event.type === 'created') {
                            setDonators(old => [...old, event.donateur])
                            setImportProgress(p => ({ ...p, current: p.current + 1 }))
                        } else if (event.type === 'skipped' || event.type === 'error') {
                            setImportProgress(p => ({ ...p, current: p.current + 1 }))
                        } else if (event.type === 'done') {
                            setImportResult({ created: event.created, skipped: event.skipped, errors: event.errors })
                            if (event.created.length > 0) {
                                justImportedRef.current = true
                                if (organisation?.type === 'église') {
                                    const updated = { ...organisation, donateurs: event.donateurs }
                                    setOrganisation(updated)
                                    sessionStorage.setItem('eglise', JSON.stringify(updated))
                                } else {
                                    setEglises(old => old.map(eg => eg._id === eglise._id ? { ...eg, donateurs: event.donateurs } : eg))
                                }
                            }
                        }
                    }
                }
            } finally {
                setImport(false)
                setImportProgress(null)
            }
        }
        reader.readAsText(file, 'utf-8')
    }

    return (
        <div style={{display:'flex', flexDirection:'column', paddingBottom:'30px'}}>
            <Header/>
            <Typography sx={{textAlign:'center', marginTop:'20px', fontSize:26}}>Gestion des Donateurs</Typography>
            {organisation&&organisation.type==='fédération'&&<TextField select size="small" label="église" sx={{alignSelf:'end',marginBottom:3, width:'25%', marginRight:3, minWidth:'200px'}} value={egliseId} onChange={e=>e.target.value==='tous'?getAllDonators():setEgliseId(old=>{if(old!==e.target.value)setDonators([]); return e.target.value})}>
                <MenuItem value={'tous'}>Tous les donateurs</MenuItem>
                {eglises.sort((a,b)=>a.nom.toLowerCase()<b.nom.toLowerCase() ? -1 : 1).map(eglise=><MenuItem key={eglise._id} value={eglise._id}>{`${eglise.nom} - ${eglise.commune.toUpperCase()}`}</MenuItem>)}
            </TextField>}
            {(eglise||donators.length>0)&&<Box sx={{paddingX:'20px', marginTop:'30px'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'20px'}}>
                    <TextField
                    size="small"
                    label="Rechercher un donateur par nom, e-mail, tel"
                    disabled={!donators?.length>0}
                    sx={{width:'60%', minWidth:'300px'}}
                    value={search_input}
                    InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search />
                          </InputAdornment>
                        ),
                    }}
                    onChange={(event)=> setSearch(event.target.value)}
                    />
                    <Stack direction='row' spacing={1}>
                        <input ref={fileInputRef} type='file' accept='.csv' style={{display:'none'}} onChange={handleImportCSV}/>
                        <Button variant='outlined' startIcon={<Download />} onClick={handleExportCSV} disabled={!eglise}>Exporter CSV</Button>
                        <Button variant='outlined' startIcon={<Upload />} onClick={()=>fileInputRef.current?.click()} disabled={!eglise||egliseId==='tous'} loading={importing}>Importer CSV</Button>
                        <Button variant='contained' startIcon={<Add />} onClick={()=>setAdd(true)}>Ajouter</Button>
                    </Stack>
                </div>
                {importProgress && <Box sx={{ marginTop: 2 }}>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 0.5 }}>
                        <Typography variant='body2' color='text.secondary'>Import en cours…</Typography>
                        <Typography variant='body2' color='text.secondary'>{importProgress.current} / {importProgress.total}</Typography>
                    </Stack>
                    <LinearProgress variant='determinate' value={importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0} />
                </Box>}
                <Box sx={{ width: '100%', marginTop:'30px' }}>
                    <Paper sx={{ width: '100%', overflow:'hidden' }}>
                        <TableContainer>
                            <Table
                                sx={{ minWidth: 700 }}
                                aria-labelledby="tableTitle"
                            >
                                <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                table={table()}
                                />
                                <TableBody>
                                {visibleRows.map((row, index) =>
                                    <StyledTableRow key={index} >
                                        {table().map((col, index)=> col.row(row, index))}
                                    </StyledTableRow>
                                )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage='lignes par page'
                        labelDisplayedRows={({ from, to, count, page }) => { return `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`; }}
                        component="div"
                        count={donators.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, npage)=> setPage(npage)}
                        onRowsPerPageChange={(event)=> {setRowsPerPage(parseInt(event.target.value, 10)); setPage(0);}}
                        />
                    </Paper>
                </Box>
            </Box>}
            <Dialog open={add||edit} maxWidth='md' fullWidth>
                {add? <DonateurForm {...{eglise, setEglises, updateDonateurs, cancel:()=>setAdd(false), updateDonateur:(n)=>setAdd(false)}}/>:
                donator&&<Paper><EditDonateurForm {...{donator, eglise, setEglises, updateDonateurs:newdonator=>{handleDelete(); setDonators(old=> [...old, newdonator])}, cancel:()=>{setEdit(false); setDonator()}, updateDonateur:(n)=>{setEdit(false); setDonator()}}}/></Paper>}
            </Dialog>
            <Dialog open={recap} onClose={()=>{setRecap(false); setDonator()}} maxWidth='lg' fullWidth>
                {donator && <RecapDonateur donateur={donator} eglise={eglise} eglises={eglises} close={()=>{setRecap(false); setDonator()}} />}
            </Dialog>
            <Dialog open={!!importResult} onClose={()=>setImportResult(null)} maxWidth='sm' fullWidth>
                <DialogTitle>Résultat de l'import</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Stack direction='row' spacing={1} flexWrap='wrap'>
                            <Chip label={`${importResult?.created?.length ?? 0} ajouté(s)`} color='success' />
                            <Chip label={`${importResult?.skipped?.length ?? 0} déjà présent(s)`} color='default' />
                            <Chip label={`${importResult?.errors?.length ?? 0} erreur(s)`} color={importResult?.errors?.length ? 'error' : 'default'} />
                        </Stack>
                        {importResult?.errors?.length > 0 && <>
                            <Divider/>
                            <Typography variant='subtitle2' color='error'>Erreurs :</Typography>
                            {importResult.errors.map((e, i) => (
                                <Typography key={i} variant='body2' color='error'>
                                    {e.nom || e.Nom || '?'} {e.prenom1 || e.Prénom || ''} — {e.reason}
                                </Typography>
                            ))}
                        </>}
                        {importResult?.skipped?.length > 0 && <>
                            <Divider/>
                            <Typography variant='subtitle2'>Déjà présents (ignorés) :</Typography>
                            {importResult.skipped.map((s, i) => (
                                <Typography key={i} variant='body2' sx={{textTransform:'capitalize'}}>
                                    {s.nom} {s.prenom1}
                                </Typography>
                            ))}
                        </>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={()=>setImportResult(null)}>Fermer</Button>
                </DialogActions>
            </Dialog>
            <Backdrop open={loading||del} >
                {loading ? <CircularProgress color="inherit" /> :
                del&&<Paper sx={{minWidth:'250px', width:'40%', paddingX:'15px', paddingY:'25px'}}>
                    <Typography variant='h5'>Supprimer un donateur</Typography>
                    <Typography variant='body1'>Voulez-vous supprimer <span style={{textTransform:'capitalize'}}>{donator.genre+' '+donator.nom+' '+donator.prenom1+(Boolean(donator.prenom2)?' '+donator.prenom2:'')}</span> de la liste des donateurs ?</Typography>
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                        <Button variant='outlined' color='carmine' onClick={()=>{setDel(false); setDonator()}}>Annuler</Button>
                        <Button variant='contained' color='carmine' onClick={handleDelete}>Supprimer</Button>
                    </Stack>
                </Paper>}
            </Backdrop>
        </div>
    );
}
